import { useEffect, useState } from "react";

import PropTypes from "prop-types";

import { handleSetAssignment } from "@/assignments/utils/assignmentActions";
import assignmentPropTypes from "@/assignments/utils/assignmentPropTypes";
import useExpirationWarning from "@/assignments/utils/useExpirationWarning";
import useHasFeature from "@/features/useHasFeature";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import AssignmentStudentPreviewToggle from "@/view/AssignmentStudentPreviewToggle";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useSetAssignmentOnLoad from "../utils/useSetAssignmentOnLoad";
import AssignmentEmbedStudentContainer from "./AssignmentEmbedStudentContainer";
import {
  rootCss,
  wrappingContainerCss,
} from "./AssignmentEmbedContainer.style";

const propTypes = {
  __cssFor: PropTypes.shape({
    root: PropTypes.object,
    wrapper: PropTypes.object,
    title: PropTypes.object,
  }),
  content: PropTypes.object,
  currentContentLevel: PropTypes.shape({
    grade_band: PropTypes.string,
    header_id: PropTypes.string,
    id: PropTypes.string,
    language: PropTypes.string,
    title: PropTypes.string,
  }),
  fetchAssignment: PropTypes.func,
  hasMultipleAssignments: PropTypes.bool,
  isSelectedAssignmentLoading: PropTypes.bool,
  latestAssignment: assignmentPropTypes,
  selectedAssignment: assignmentPropTypes,
};

const AssignmentEmbedContainer = ({
  __cssFor,
  content,
  currentContentLevel,
  fetchAssignment,
  hasMultipleAssignments,
  isSelectedAssignmentLoading,
  latestAssignment,
  selectedAssignment,
}) => {
  // Hooks
  const { data: user } = useUser();
  const { isStudent, isTeacher } = getUserProperties(user);
  const { setIsAssignmentLevelLockedForStudentPreview } =
    useStudentPreviewContext();
  const [hasStudentPreviewFeature] = useHasFeature("student_preview");
  const { updatedAssignment, dispatch } = useAssignmentReducerContext();

  // The embed should only be set as loading on first load of the assignment.
  // It should not show a loading state when refetching the assignment post-save.
  const [isLoading, setIsLoading] = useState(true);

  useSetAssignmentOnLoad({
    fetchAssignment,
    handleSetAssignment: (formattedAssignment) =>
      handleSetAssignment(formattedAssignment, dispatch),
    isSelectedAssignmentLoading,
    isStudent,
    latestAssignment,
    selectedAssignment,
    setIsLoading,
  });

  useExpirationWarning(content?.data);

  // Calculated variables:
  const currentContentLevelId = currentContentLevel?.id;

  useEffect(() => {
    setIsAssignmentLevelLockedForStudentPreview(
      updatedAssignment?.is_reading_level_locked,
    );
  }, [JSON.stringify(updatedAssignment)]);

  // TODO: Handle loading view here.
  if (isLoading) return null;

  return (
    <section css={[rootCss, __cssFor?.root]}>
      <div css={wrappingContainerCss}>
        {hasStudentPreviewFeature && isTeacher && (
          <AssignmentStudentPreviewToggle
            currentContentLevel={currentContentLevel}
          />
        )}
        <AssignmentEmbedStudentContainer
          __cssFor={__cssFor}
          assignment={updatedAssignment}
          currentContentLevelId={currentContentLevelId}
          hasMultipleAssignments={hasMultipleAssignments}
        />
      </div>
    </section>
  );
};

AssignmentEmbedContainer.propTypes = propTypes;

export default AssignmentEmbedContainer;
